import React, { useEffect, useRef, useState } from "react";
import Icon from "./icons";
import { failureText, isSendable, itemPositions, kindOf, needsType } from "../../../lib/artemis/scratchpad";
import { overlayFeatures, OVERLAY_FILL, OVERLAY_LINE } from "../../../action/artemis-actions/agent-actions";
import { imageryAndTopographyOthers, multiTile, normalizeCloudfrontUrl, overlayMaxZoom } from "../../map/render-layers/map-types";
import { isTopoLinesLayer, topoLinesLayerIdForZoom } from "../../../lib/topoLineLayers";
import { drawnOverlayIds, GOOGLE_BASEMAP_TYPES } from "../../../lib/artemis/scratchLayers";
import { moveMap } from "../../../lib/artemis/map-view";
import { markStartView } from "../../../lib/artemis/context";

// An overlay for the scratchpad map. Cloud-tiled layers get their own ImageMapType here: the main map's
// factory (multiTileLayersXYZ) keeps one zoom listener per layer id for the whole app, so using it on a
// second map would unhook the main map's. Server-rendered layers use multiTile, which takes the map.
const scratchOverlay = (google, layer, map, filters, layersDictionary) => {
  if (!layer.cloudfrontUrl) return multiTile(layer, { mapInstance: map, filters }, true)
  const pinned = normalizeCloudfrontUrl(layer.cloudfrontUrl)
  const urlFor = zoom => {
    if (!isTopoLinesLayer(layer.id)) return pinned
    // Topo lines come as two builds by zoom (see multiTileLayersXYZ).
    const id = topoLinesLayerIdForZoom(zoom)
    if (id === null) return null
    const sibling = id === layer.id ? layer : layersDictionary[id]
    return sibling && sibling.cloudfrontUrl ? normalizeCloudfrontUrl(sibling.cloudfrontUrl) : pinned
  }
  return new google.ImageMapType({
    getTileUrl: (coord, zoom) => {
      const url = urlFor(zoom)
      if (!url) return ''
      return url.replace('{x}', coord.x).replace('{y}', (1 << zoom) - 1 - coord.y).replace('{z}', zoom)
    },
    tileSize: new google.Size(256, 256),
    minZoom: 3,
    maxZoom: overlayMaxZoom(layer),
    name: layer.name,
    alt: { id: layer.id, drawOrder: layer.drawOrder }
  })
}

// The scratchpad map's own layers menu: a basemap, and overlays to toggle.
const LayersMenu = ({ choices, onPickBasemap, onToggleOverlay }) => {
  const [open, setOpen] = useState(false)
  const onCount = choices.overlays.filter(o => o.on).length
  return (
    <div className="artemis-scratch-layers">
      <button type="button" className="artemis-scratch-layers-button" aria-expanded={open} onClick={() => setOpen(o => !o)} title="Layers on this map">
        <Icon name="layer" size={15} />{onCount > 0 ? onCount : ''}
      </button>
      {open && (
        <div className="artemis-scratch-layers-menu" role="dialog" aria-label="Scratchpad map layers">
          <span className="artemis-eyebrow">Basemap</span>
          <select className="artemis-scratch-layers-select" value={(choices.basemaps.find(b => b.on) || {}).id || ''}
            onChange={e => onPickBasemap(Number(e.target.value))}>
            {choices.basemaps.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <span className="artemis-eyebrow">Layers</span>
          {choices.overlays.length === 0 && <p className="artemis-muted">Turn layers on in the map’s layer menu, or ask Artemis, and they show here.</p>}
          {choices.overlays.map(o => (
            <label key={o.id} className="artemis-scratch-layers-row">
              <input type="checkbox" checked={o.on} onChange={() => onToggleOverlay(o.id, !o.on)} />
              <span>{o.name}</span>
            </label>
          ))}
          <p className="artemis-scratch-layers-note">Only this map. Your main map’s layers stay as they are.</p>
        </div>
      )}
    </div>
  )
}

// The full-screen view's scratchpad column (Basemap Home v2): a small map of the drafts, the list
// with a tick box each, and Send to map. Nothing reaches the user's map until they send.

// Where the scratchpad map opens when the main map's view isn't known.
const DEFAULT_START = { lat: 44, lng: -110 }
const DEFAULT_START_ZOOM = 5

const KIND_ICON = { waypoint: 'pin', route: 'arrow', shape: 'shape', chart: 'chart', note: 'chat' }
const DRAFT_COLOR = '#9ECC3B'

// Draws the items on a Google map (the app's map library) and fits them; focus(id) flies to one.
// preview is a hunt card's "Show on map" focus ({ bbox, point, fill, outline, pins }): in the full view
// the main map is hidden, so the card's unit or hunt area is drawn and zoomed to here instead.
// layers ({ basemapId, overlayIds }) are this map's own; view ({ n, view }) is a move Artemis asked for
// in the full view; onMap hands the map to the chat (for Undo's "where it was").
// startView ({ center: [lng, lat], zoom }) is where the user's main map is: the map opens there, so a
// question asked before anything is drawn is about that place (WEB-7172).
const ScratchMap = ({ items, focusId, preview, layers, layersDictionary = {}, filters, view, startView, onMap, layerChoices, onPickBasemap, onToggleOverlay }) => {
  const el = useRef(null)
  const map = useRef(null)
  const drawn = useRef([])
  const previewLayer = useRef(null)
  const overlays = useRef(new Map())
  const filtersRef = useRef(filters)
  filtersRef.current = filters
  const [ready, setReady] = useState(false)
  const google = typeof window !== 'undefined' && window.google && window.google.maps ? window.google.maps : null

  useEffect(() => {
    if (!google || !el.current || map.current) return
    const start = startView && Array.isArray(startView.center)
      ? { lat: startView.center[1], lng: startView.center[0], zoom: Number.isFinite(startView.zoom) ? Math.round(startView.zoom) : 10 }
      : null
    map.current = new google.Map(el.current, {
      mapTypeId: 'hybrid', disableDefaultUI: true, zoomControl: true, gestureHandling: 'cooperative',
      center: start ? { lat: start.lat, lng: start.lng } : DEFAULT_START, zoom: start ? start.zoom : DEFAULT_START_ZOOM
    })
    // Without the user's place it opens over the West, which isn't where they are.
    if (!start) markStartView(map.current, { ...DEFAULT_START, zoom: DEFAULT_START_ZOOM })
    setReady(true)
    if (onMap) onMap(map.current)
    return () => { if (onMap) onMap(null) }
  }, [google])

  // The wide view (and the window) resize the box: keep the same center and redraw to fill it.
  useEffect(() => {
    if (!google || !el.current || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(() => {
      if (!map.current) return
      const center = map.current.getCenter()
      google.event.trigger(map.current, 'resize')
      if (center) map.current.setCenter(center)
    })
    observer.observe(el.current)
    return () => observer.disconnect()
  }, [google])

  // The basemap: Google's own types, or a BaseMap basemap drawn as this map's map type.
  const basemapId = layers ? layers.basemapId : null
  useEffect(() => {
    if (!ready || basemapId == null) return
    if (GOOGLE_BASEMAP_TYPES[basemapId]) {
      // A BaseMap basemap may have lowered the zoom limit; Google's go to 24, as on the main map.
      map.current.setOptions({ maxZoom: 24 })
      map.current.setMapTypeId(GOOGLE_BASEMAP_TYPES[basemapId])
    }
    else if (layersDictionary[basemapId]) imageryAndTopographyOthers(layersDictionary[basemapId], { mapInstance: map.current })
  }, [ready, basemapId, layersDictionary])

  // Overlays, in the layer menu's draw order. Built once per layer and kept while the map lives.
  const overlayKey = layers ? drawnOverlayIds(layers, layersDictionary).join(',') : ''
  useEffect(() => {
    if (!ready || !layers) return
    const wanted = drawnOverlayIds(layers, layersDictionary).map(id => layersDictionary[id]).filter(Boolean)
      .sort((a, b) => (a.drawOrder ?? 0) - (b.drawOrder ?? 0))
    map.current.overlayMapTypes.clear()
    wanted.forEach(layer => {
      if (!overlays.current.has(layer.id)) {
        try {
          overlays.current.set(layer.id, scratchOverlay(google, layer, map.current, () => filtersRef.current || [], layersDictionary))
        } catch (e) {
          console.warn('The scratchpad map could not draw layer', layer.id, e)
          return
        }
      }
      map.current.overlayMapTypes.push(overlays.current.get(layer.id))
    })
  }, [ready, overlayKey, layersDictionary])

  useEffect(() => {
    if (ready && view && view.view) moveMap(map.current, view.view)
  }, [ready, view && view.n])

  useEffect(() => {
    if (!google || !map.current) return
    drawn.current.forEach(o => o.setMap(null))
    drawn.current = []
    const bounds = new google.LatLngBounds()
    items.forEach(item => {
      const g = item.Geometry
      if (!g) return
      const color = item.Status === 'sent' ? '#8a8a8a' : (item.Color || DRAFT_COLOR)
      const path = itemPositions(item).map(([lng, lat]) => ({ lat, lng }))
      path.forEach(p => bounds.extend(p))
      if (g.type === 'Point' && item.Data && item.Data.RadiusMeters) {
        drawn.current.push(new google.Circle({ map: map.current, center: path[0], radius: item.Data.RadiusMeters, strokeColor: color, strokeWeight: 2, fillColor: color, fillOpacity: 0.15 }))
      } else if (g.type === 'Point') {
        drawn.current.push(new google.Marker({
          map: map.current, position: path[0], title: item.Title,
          icon: { path: google.SymbolPath.CIRCLE, scale: 6, fillColor: color, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 }
        }))
      } else if (g.type === 'LineString') {
        drawn.current.push(new google.Polyline({ map: map.current, path, strokeColor: '#90FF00', strokeWeight: 3 }))
      } else if (g.type === 'Polygon' || g.type === 'MultiPolygon') {
        const rings = g.type === 'Polygon' ? g.coordinates : g.coordinates.flat()
        drawn.current.push(new google.Polygon({ map: map.current, paths: rings.map(r => r.map(([lng, lat]) => ({ lat, lng }))), strokeColor: color, strokeWeight: 2, fillColor: color, fillOpacity: 0.15 }))
      }
    })
    if (!bounds.isEmpty()) {
      map.current.fitBounds(bounds, 34)
      if (map.current.getZoom() > 14) map.current.setZoom(14)
    }
  }, [google, items])

  useEffect(() => {
    if (!google || !map.current || !focusId) return
    const item = items.find(i => i.Id === focusId)
    const points = itemPositions(item)
    if (points.length === 0) return
    const bounds = new google.LatLngBounds()
    points.forEach(([lng, lat]) => bounds.extend({ lat, lng }))
    if (points.length === 1) { map.current.panTo(bounds.getCenter()); map.current.setZoom(14) } else map.current.fitBounds(bounds, 30)
  }, [google, focusId])

  useEffect(() => {
    if (!google || !map.current) return
    if (previewLayer.current) { previewLayer.current.setMap(null); previewLayer.current = null }
    if (!preview) return
    const layer = new google.Data({ map: map.current })
    layer.addGeoJson(overlayFeatures(preview))
    layer.setStyle(feature => {
      const role = feature.getProperty('role')
      if (role === 'pin') return { icon: { path: google.SymbolPath.CIRCLE, scale: 6, fillColor: OVERLAY_FILL, fillOpacity: 1, strokeColor: '#000', strokeWeight: 1.5 }, zIndex: 3 }
      return role === 'fill'
        ? { fillColor: OVERLAY_FILL, fillOpacity: 0.28, strokeColor: OVERLAY_FILL, strokeWeight: 1, zIndex: 2, clickable: false }
        : { fillOpacity: 0, strokeColor: OVERLAY_LINE, strokeWeight: 2, zIndex: 2, clickable: false }
    })
    previewLayer.current = layer
    const bbox = Array.isArray(preview.bbox) && preview.bbox.length === 4 ? preview.bbox : null
    if (bbox && bbox[0] !== bbox[2]) {
      const [w, s, e, n] = bbox
      map.current.fitBounds(new google.LatLngBounds({ lat: s, lng: w }, { lat: n, lng: e }), 20)
    } else if (Array.isArray(preview.point) && preview.point.length === 2) {
      map.current.panTo({ lat: preview.point[1], lng: preview.point[0] })
      map.current.setZoom(Math.max(map.current.getZoom() || 0, 11))
    }
  }, [google, preview])

  if (!google) return null
  return (
    <div className="artemis-scratch-map-wrap">
      <div className="artemis-scratch-map" ref={el} />
      {layerChoices && <LayersMenu choices={layerChoices} onPickBasemap={onPickBasemap} onToggleOverlay={onToggleOverlay} />}
    </div>
  )
}

const ScratchRow = ({ item, checked, failure, typeOptions, onToggle, onRemove, onFocus, onSetType }) => {
  const kind = kindOf(item)
  const sent = item.Status === 'sent'
  const canSend = !sent && isSendable(item)
  return (
    <div className={`artemis-scratch-row ${sent ? 'artemis-scratch-row-sent' : ''}`}>
      {canSend
        ? (
          <button type="button" role="checkbox" aria-checked={checked} aria-label={`Send ${item.Title}`}
            className={`artemis-scratch-check ${checked ? 'artemis-scratch-check-on' : ''}`} onClick={() => onToggle(item.Id)}>
            {checked && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5 9-10" /></svg>}
          </button>
        )
        : <span className="artemis-scratch-check-spacer" />}
      <span className={`artemis-scratch-icon artemis-scratch-icon-${kind}`}><Icon name={KIND_ICON[kind]} size={16} /></span>
      <span className="artemis-scratch-text">
        <button type="button" className="artemis-scratch-focus" onClick={() => onFocus(item.Id)}>
          <span className="artemis-scratch-title">{item.Title}</span>
          <span className={`artemis-scratch-sub ${failure ? 'artemis-scratch-sub-failed' : ''}`}>{failure ? failureText(failure) : (item.Subtitle || (kind === 'note' ? 'Note' : ''))}</span>
        </button>
        {/* A waypoint needs a type before it can go to the map. */}
        {needsType(item) && typeOptions.length > 0 && (
          <select className="artemis-scratch-type" value="" aria-label={`Waypoint type for ${item.Title}`}
            onChange={e => e.target.value && onSetType(item.Id, Number(e.target.value))}>
            <option value="">Pick a waypoint type…</option>
            {typeOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
      </span>
      {sent && <span className="artemis-scratch-tag">ON MAP</span>}
      <button type="button" className="artemis-icon-button artemis-icon-button-sm" title="Remove from the scratchpad" aria-label={`Remove ${item.Title}`} onClick={() => onRemove(item.Id)}>
        <Icon name="close" size={14} />
      </button>
    </div>
  )
}

// map: the scratchpad map's own layers and moves ({ layers, layersDictionary, filters, view, onMap,
// layerChoices, onPickBasemap, onToggleOverlay }), from the chat.
// expanded: the wide view, where the map takes most of the column (onToggleExpand switches it).
const Scratchpad = ({ scratch, typeOptions = [], onSend, onRemove, onSetType, onClose, preview = null, map = {}, expanded = false, onToggleExpand }) => {
  const [focusId, setFocusId] = React.useState(null)
  const { items, checked, failed, checkedCount, allChecked, sending, state } = scratch
  const count = items.length
  return (
    <aside className={`artemis-panel artemis-scratch ${expanded ? 'artemis-scratch-expanded' : ''}`} aria-label="Scratchpad">
      <div className="artemis-scratch-head">
        <span className="artemis-sessions-title">Scratchpad</span>
        {count > 0 && <span className="artemis-scratch-count">{count} {count === 1 ? 'item' : 'items'}</span>}
        {onToggleExpand && (
          <button type="button" className="artemis-icon-button artemis-icon-button-sm" aria-pressed={expanded}
            title={expanded ? 'Make the map smaller' : 'Make the map bigger'} aria-label={expanded ? 'Make the map smaller' : 'Make the map bigger'} onClick={onToggleExpand}>
            <Icon name={expanded ? 'collapse' : 'expand'} size={15} />
          </button>
        )}
        {onClose && (
          <button type="button" className="artemis-icon-button artemis-icon-button-sm" title="Hide the scratchpad" aria-label="Hide the scratchpad" onClick={onClose}>
            <Icon name="close" size={16} />
          </button>
        )}
      </div>
      <ScratchMap items={items} focusId={focusId} preview={preview} {...map} />
      <p className="artemis-scratch-note">Drafts from this session. Nothing is saved to your map until you send it.</p>
      <div className="artemis-scratch-list">
        {items.map(item => (
          <ScratchRow key={item.Id} item={item} checked={checked.has(item.Id)} failure={failed[item.Id]} typeOptions={typeOptions}
            onToggle={scratch.toggle} onRemove={onRemove} onFocus={setFocusId} onSetType={onSetType} />
        ))}
        {count === 0 && (
          <p className="artemis-scratch-empty">
            {state === 'loading' ? 'Loading the scratchpad…' : state === 'failed' ? 'Couldn’t load the scratchpad.' : 'Ask Artemis for waypoints, routes or glassing spots. They collect here until you send them to your map.'}
          </p>
        )}
      </div>
      <div className="artemis-scratch-foot">
        <button type="button" className="artemis-link-button artemis-scratch-all" onClick={scratch.toggleAll} disabled={count === 0}>
          {allChecked ? 'Clear selection' : 'Select all'}
        </button>
        <span className="artemis-flex" />
        <button type="button" className="artemis-scratch-send" disabled={checkedCount === 0 || sending} onClick={onSend}>
          <Icon name="map" size={16} />
          {sending ? 'Sending…' : checkedCount ? `Send ${checkedCount} to map` : 'Send to map'}
        </button>
      </div>
    </aside>
  )
}

export default Scratchpad
